import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { FaArrowUp, FaArrowDown, FaEquals, FaChartLine, FaHistory, FaUserPlus, FaCopy, FaCheck } from 'react-icons/fa';
import { SiTether } from 'react-icons/si';
import { useT } from '../../../i18n';
import bitcoin from '../../../assets/utility/bitcoin.png';
import { useGetLastOraclePrice, useGetCurrentEpoch, useGetRound, useGetConfig } from '../context/PredictionsContext';
import { useUSDTBalance } from '../hooks/useUSDTBalance';
import { priceSide, formatUsdt } from '../utils/roundMath';
import { AnimatedPrice } from './Effects';
import { referralLink } from '../../../referral';

const SIDE = {
  up: ['text-emerald-400', FaArrowUp, 'bvb.up'],
  down: ['text-rose-400', FaArrowDown, 'bvb.down'],
  tie: ['text-amber-300', FaEquals, 'bvb.tie'],
};

/** Invite link: referrers earn the referral fee of every bet of the players they bring */
const InviteButton = ({ compact }) => {
  const t = useT();
  const { address } = useAccount();
  const config = useGetConfig();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  // one link for the whole platform: the referrer earns in every game the player joins
  const link = referralLink(address);

  const copy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-xl border border-brand/40 bg-brand/15 px-3 py-2 text-sm font-semibold text-[#d9c5f0] hover:bg-brand/25"
      >
        <FaUserPlus /> {!compact && t('bvb.invite')}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-2 w-72 rounded-2xl border border-white/10 bg-[#0f0a14] p-4 shadow-2xl">
          <p className="text-sm font-bold text-white">{t('bvb.invite')}</p>
          <p className="mt-1 text-xs text-white/55">{t('bvb.inviteBody', { pct: (config.referralBps ?? 200) / 100 })}</p>
          {address ? (
            <div className="mt-3 flex items-center gap-2">
              <input readOnly value={link} className="min-w-0 flex-1 rounded-lg border border-white/10 bg-black/50 px-2 py-1.5 text-xs text-white/70" />
              <button onClick={copy} className="rounded-lg bg-brand px-3 py-1.5 text-xs font-bold text-white">
                {copied ? <FaCheck /> : <FaCopy />}
              </button>
            </div>
          ) : (
            <p className="mt-3 text-xs text-amber-200">{t('bvb.connectForLink')}</p>
          )}
          <Link to="/referral" className="lb-link mt-3 inline-block text-xs">
            {t('referral.seeAll')}
          </Link>
        </div>
      )}
    </div>
  );
};

/**
 * Game status bar: BTC price, which side is winning the live round right now and when it ends, the player's USDT
 * balance, the invite link and the chart/history toggles.
 */
const GameBar = ({ onToggleChart, chartOpen, onToggleHistory, historyOpen, compact = false }) => {
  // without onToggleChart (phones: tabs below the bar) only the invite button is shown
  const t = useT();
  const { address } = useAccount();
  const { getUSDTBalance } = useUSDTBalance();
  const price = useGetLastOraclePrice();
  const currentEpoch = Number(useGetCurrentEpoch() ?? 0);
  const live = useGetRound(currentEpoch - 1);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    let active = true;
    if (address) getUSDTBalance(address).then((b) => active && setBalance(b));
    else setBalance(null);
    const id = address ? setInterval(() => getUSDTBalance(address).then((b) => active && setBalance(b)), 15000) : null;
    return () => {
      active = false;
      if (id) clearInterval(id);
    };
    // getUSDTBalance is recreated on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  // the side winning the live round: its lock price against the oracle price now
  const side = live && Number(live.lockPrice) > 0 ? priceSide(price, live.lockPrice) : null;
  const [color, Icon, labelKey] = SIDE[side || 'tie'];

  const toggles = (
    <div className="flex items-center gap-2">
      {onToggleChart && (
      <button
        onClick={onToggleChart}
        aria-label={chartOpen ? t('bvb.hideChart') : t('bvb.showChart')}
        className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${
          chartOpen ? 'bg-brand text-white' : 'bg-white/5 text-white/70 hover:bg-white/10'
        }`}
      >
        <FaChartLine /> {!compact && (chartOpen ? t('bvb.hideChart') : t('bvb.showChart'))}
      </button>
      )}
      {onToggleHistory && (
        <button
          onClick={onToggleHistory}
          aria-label={t('bvb.history')}
          className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${
            historyOpen ? 'bg-brand text-white' : 'bg-white/5 text-white/70 hover:bg-white/10'
          }`}
        >
          <FaHistory /> {!compact && t('bvb.history')}
        </button>
      )}
      <InviteButton compact={compact} />
    </div>
  );

  return (
    <div className={`flex flex-wrap items-center gap-3 border-b border-white/5 ${compact ? 'p-3' : 'px-5 py-3'}`}>
      <div className="flex items-center gap-2.5">
        <img src={bitcoin} alt="" className="h-8 w-8" />
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/40">BTC/USD</div>
          <div className="text-lg font-extrabold text-[#F7931A]">
            <AnimatedPrice value={price} className="-ml-1.5" />
          </div>
        </div>
      </div>

      {side && (
        <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/30 px-3 py-1.5">
          <span className="text-[11px] uppercase tracking-wider text-white/40">#{currentEpoch - 1}</span>
          <span className={`flex items-center gap-1 text-sm font-bold ${color}`}>
            <Icon size={11} /> {t(labelKey)}
            {side !== 'tie' && <span className="font-medium lowercase text-white/55">{t('bvb.winningNow')}</span>}
          </span>
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-300" />
        </div>
      )}

      <div className="ml-auto flex items-center gap-3">
        {address && balance !== null && !compact && (
          <div className="flex items-center gap-1.5 text-sm">
            <SiTether className="text-[#26A17B]" />
            <span className="lb-num font-bold text-white">{formatUsdt(balance)}</span>
          </div>
        )}
        {toggles}
      </div>
    </div>
  );
};

export default GameBar;
